import { useCallback, useEffect, useMemo, useState } from 'react';
import './TradingSettings.css';
import { hasManualTradingOrigin } from '@/config/api';
import {
  deleteExchangeLeverageLimit,
  listExchangeAccounts,
  listExchangeLeverageLimits,
  saveExchangeAccount,
  upsertExchangeLeverageLimit,
  type ExchangeAccountView,
  type ExchangeLeverageLimitView,
} from '@/services/manualTradingApi';

const EXCHANGE_OPTIONS = [
  'Binance',
  'Bybit',
  'Gateio',
  'Bitget',
  'OKX',
  'Aster',
  'EdgeX',
  'Hyperliquid',
  'Lighter',
  'Paradex',
] as const;

const CATEGORY_LABEL: Record<'Spot' | 'Linear' | 'Inverse' | string, string> = {
  Linear: 'U 本位永续',
  Inverse: '币本位合约',
  Spot: '现货',
};

function getStatusClass(status: string): string {
  const normalized = status.toLowerCase();
  if (normalized.includes('config') || normalized.includes('active') || normalized.includes('ready')) {
    return 'ready';
  }
  if (normalized.includes('error') || normalized.includes('fail') || normalized.includes('disabled')) {
    return 'danger';
  }
  return 'neutral';
}

function formatLeverageHint(maxLeverage: number, equityUsd?: number): string {
  if (!Number.isFinite(maxLeverage) || maxLeverage <= 0) return '';
  if (equityUsd != null && equityUsd > 0) {
    const cap = equityUsd * maxLeverage;
    return `≈ ${cap.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })} 上限`;
  }
  return `仓位名义价值上限 = 账户权益 × ${maxLeverage}`;
}

export function TradingSettings() {
  const [exchange, setExchange] = useState<string>('Binance');
  const [category, setCategory] = useState<'Spot' | 'Linear' | 'Inverse'>('Linear');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [passphrase, setPassphrase] = useState('');

  const [accounts, setAccounts] = useState<ExchangeAccountView[]>([]);
  const [leverageLimits, setLeverageLimits] = useState<ExchangeLeverageLimitView[]>([]);
  const [leverageDrafts, setLeverageDrafts] = useState<Record<string, string>>({});
  const [leverageBusyExchange, setLeverageBusyExchange] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [leverageInfo, setLeverageInfo] = useState<string | null>(null);
  const [leverageError, setLeverageError] = useState<string | null>(null);

  const leverageByExchange = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of leverageLimits) {
      map.set(row.exchange, row.max_leverage);
    }
    return map;
  }, [leverageLimits]);

  const configuredExchangeNames = useMemo(
    () => Array.from(new Set(accounts.map((account) => account.exchange))).sort((a, b) => a.localeCompare(b)),
    [accounts]
  );

  const configuredLeverageCount = useMemo(
    () => configuredExchangeNames.filter((exchangeName) => leverageByExchange.has(exchangeName)).length,
    [configuredExchangeNames, leverageByExchange]
  );

  const refreshAccounts = useCallback(async () => {
    if (!hasManualTradingOrigin()) return;
    setBusy(true);
    setError(null);
    try {
      const rows = await listExchangeAccounts();
      setAccounts(rows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }, []);

  const refreshLeverageLimits = useCallback(async () => {
    if (!hasManualTradingOrigin()) return;
    setLeverageError(null);
    try {
      const rows = await listExchangeLeverageLimits();
      setLeverageLimits(rows);
      setLeverageDrafts((prev) => {
        const next = { ...prev };
        for (const row of rows) {
          if (next[row.exchange] == null) {
            next[row.exchange] = String(row.max_leverage);
          }
        }
        return next;
      });
    } catch (e) {
      setLeverageError(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([refreshAccounts(), refreshLeverageLimits()]);
  }, [refreshAccounts, refreshLeverageLimits]);

  useEffect(() => {
    void refreshAll();
  }, [refreshAll]);

  useEffect(() => {
    setLeverageDrafts((prev) => {
      const next = { ...prev };
      for (const account of accounts) {
        if (next[account.exchange] != null) continue;
        const saved = leverageByExchange.get(account.exchange);
        next[account.exchange] = saved != null ? String(saved) : '';
      }
      return next;
    });
  }, [accounts, leverageByExchange]);

  const onSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    setInfo(null);
    if (!hasManualTradingOrigin()) return;
    if (!exchange || !apiKey.trim() || !secret.trim()) {
      setError('请填写交易所、API Key 与 Secret');
      return;
    }
    setBusy(true);
    try {
      await saveExchangeAccount({
        exchange,
        api_key: apiKey.trim(),
        secret: secret.trim(),
        passphrase: passphrase.trim(),
        category,
      });
      setInfo(`已保存 ${exchange} （${category}）`);
      setApiKey('');
      setSecret('');
      setPassphrase('');
      await refreshAll();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onSaveLeverage = async (exchangeName: string) => {
    setLeverageError(null);
    setLeverageInfo(null);
    const raw = leverageDrafts[exchangeName]?.trim() ?? '';
    const maxLeverage = Number(raw);
    if (!raw || !Number.isFinite(maxLeverage) || maxLeverage <= 0) {
      setLeverageError(`${exchangeName}：请输入大于 0 的杠杆倍数`);
      return;
    }
    setLeverageBusyExchange(exchangeName);
    try {
      const saved = await upsertExchangeLeverageLimit({
        exchange: exchangeName,
        max_leverage: maxLeverage,
      });
      setLeverageLimits((prev) => {
        const others = prev.filter((row) => row.exchange !== saved.exchange);
        return [...others, saved].sort((a, b) => a.exchange.localeCompare(b.exchange));
      });
      setLeverageDrafts((prev) => ({ ...prev, [exchangeName]: String(saved.max_leverage) }));
      setLeverageInfo(`已保存 ${exchangeName} 最大杠杆 ${saved.max_leverage}x`);
    } catch (e) {
      setLeverageError(e instanceof Error ? e.message : String(e));
    } finally {
      setLeverageBusyExchange(null);
    }
  };

  const onClearLeverage = async (exchangeName: string) => {
    setLeverageError(null);
    setLeverageInfo(null);
    setLeverageBusyExchange(exchangeName);
    try {
      await deleteExchangeLeverageLimit(exchangeName);
      setLeverageLimits((prev) => prev.filter((row) => row.exchange !== exchangeName));
      setLeverageDrafts((prev) => ({ ...prev, [exchangeName]: '' }));
      setLeverageInfo(`已清除 ${exchangeName} 杠杆上限，该交易所不再限制开仓`);
    } catch (e) {
      setLeverageError(e instanceof Error ? e.message : String(e));
    } finally {
      setLeverageBusyExchange(null);
    }
  };

  if (!hasManualTradingOrigin()) {
    return (
      <div className="user-trading-settings">
        <section className="settings-hero">
          <div>
            <p className="settings-kicker">账户安全</p>
            <h2>套利账户 · 交易所密钥</h2>
            <p>连接交易所私有接口前，需要先配置交易服务地址。</p>
          </div>
        </section>
        <p className="warn">
          未配置 <code>VITE_MANUAL_TRADING_ORIGIN</code>。请在前端环境中指向 Rust `trading-engine` 服务的域名后再使用户保存 API key。
        </p>
      </div>
    );
  }

  return (
    <div className="user-trading-settings">
      <section className="settings-hero">
        <div className="settings-hero-copy">
          <p className="settings-kicker">账户安全</p>
          <h2>套利账户 · 交易所密钥</h2>
          <p>集中管理交易所 API Key、产品类型和按所仓位杠杆上限。密钥只保存不完整回显，风险参数会在 loadme 后自动生效。</p>
        </div>
        <div className="settings-summary-grid" aria-label="配置概览">
          <div className="settings-summary-item">
            <span>交易所</span>
            <strong>{configuredExchangeNames.length}</strong>
          </div>
          <div className="settings-summary-item">
            <span>密钥组</span>
            <strong>{accounts.length}</strong>
          </div>
          <div className="settings-summary-item">
            <span>杠杆限制</span>
            <strong>
              {configuredLeverageCount}/{configuredExchangeNames.length || 0}
            </strong>
          </div>
        </div>
      </section>

      <div className="settings-content-grid">
        <section className="settings-panel key-panel">
          <div className="settings-panel-head">
            <div>
              <p className="settings-section-eyebrow">新增或更新</p>
              <h3>交易所密钥</h3>
            </div>
            <span className="settings-pill">Secret 加密存储</span>
          </div>

          <form onSubmit={(e) => void onSave(e)} className="exchange-form">
            <div className="exchange-form-grid">
              <label>
                <span>交易所</span>
                <select value={exchange} onChange={(e) => setExchange(e.target.value)}>
                  {EXCHANGE_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>产品类型</span>
                <select value={category} onChange={(e) => setCategory(e.target.value as 'Spot' | 'Linear' | 'Inverse')}>
                  <option value="Linear">U 本位永续 Linear</option>
                  <option value="Inverse">币本位 Inverse</option>
                  <option value="Spot">现货 Spot</option>
                </select>
              </label>
              <label className="wide">
                <span>API Key</span>
                <input value={apiKey} autoComplete="off" onChange={(e) => setApiKey(e.target.value)} placeholder="粘贴 API Key" />
              </label>
              <label>
                <span>Secret</span>
                <input
                  type="password"
                  value={secret}
                  autoComplete="off"
                  onChange={(e) => setSecret(e.target.value)}
                  placeholder="粘贴 Secret"
                />
              </label>
              <label>
                <span>Passphrase</span>
                <input value={passphrase} autoComplete="off" onChange={(e) => setPassphrase(e.target.value)} placeholder="OKX / Bitget 可选" />
              </label>
            </div>

            <div className="form-footer">
              <button type="submit" className="primary" disabled={busy}>
                {busy ? '保存中…' : '保存密钥'}
              </button>
              <p>保存后会覆盖同交易所、同产品类型的旧密钥。</p>
            </div>
          </form>

          {error ? <p className="fail">{error}</p> : null}
          {info ? <p className="msg">{info}</p> : null}
        </section>

        <section className="settings-panel accounts-panel">
          <div className="settings-panel-head">
            <div>
              <p className="settings-section-eyebrow">当前状态</p>
              <h3>已配置交易所</h3>
            </div>
            <button type="button" className="secondary compact" onClick={() => void refreshAccounts()} disabled={busy}>
              刷新
            </button>
          </div>

          {!accounts.length ? (
            <div className="settings-empty">
              <strong>暂无密钥</strong>
              <span>先保存一个交易所密钥，交易面板才会启用私网能力。</span>
            </div>
          ) : (
            <div className="account-card-list">
              {accounts.map((a) => (
                <article key={`${a.exchange}-${a.category}`} className="account-card">
                  <div className="account-card-main">
                    <div>
                      <strong>{a.exchange}</strong>
                      <span>{CATEGORY_LABEL[a.category] ?? a.category}</span>
                    </div>
                    <span className={`status-badge ${getStatusClass(a.status)}`}>{a.status}</span>
                  </div>
                  <div className="account-key-row">
                    <span>API Key</span>
                    <code>{a.api_key_label}</code>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </div>

      <section className="settings-panel leverage-limits">
        <div className="settings-panel-head leverage-limits-head">
          <div>
            <p className="settings-section-eyebrow">风控参数</p>
            <h3>仓位杠杆上限</h3>
            <p className="lead-small">当交易所仓位名义价值超过「账户权益 × 杠杆」后，系统会拒绝所有涉及该所的开仓。</p>
          </div>
          <button type="button" className="secondary" onClick={() => void refreshLeverageLimits()} disabled={busy}>
            刷新限制
          </button>
        </div>

        {configuredExchangeNames.length === 0 ? (
          <div className="settings-empty">
            <strong>等待交易所密钥</strong>
            <span>请先配置至少一个交易所 API Key。</span>
          </div>
        ) : (
          <div className="leverage-limit-list">
            {configuredExchangeNames.map((exchangeName) => {
              const saved = leverageByExchange.get(exchangeName);
              const draft = leverageDrafts[exchangeName] ?? (saved != null ? String(saved) : '');
              const draftNum = Number(draft);
              const rowBusy = leverageBusyExchange === exchangeName;
              return (
                <article key={exchangeName} className="leverage-limit-row">
                  <div className="leverage-limit-exchange">
                    <strong>{exchangeName}</strong>
                    <span className={saved != null ? 'leverage-limit-status set' : 'leverage-limit-status'}>
                      {saved != null ? `已限制 ${saved}x` : '未设置上限'}
                    </span>
                  </div>
                  <div className="leverage-limit-control">
                    <label className="leverage-limit-input-wrap">
                      <span>最大杠杆</span>
                      <div className="leverage-limit-input-group">
                        <input
                          type="number"
                          min="0"
                          step="0.1"
                          inputMode="decimal"
                          value={draft}
                          disabled={rowBusy}
                          placeholder="例如 3"
                          onChange={(e) =>
                            setLeverageDrafts((prev) => ({
                              ...prev,
                              [exchangeName]: e.target.value,
                            }))
                          }
                        />
                        <span className="leverage-limit-suffix">x</span>
                      </div>
                    </label>
                    <div className="leverage-limit-actions">
                      <button
                        type="button"
                        className="primary compact"
                        disabled={rowBusy}
                        onClick={() => void onSaveLeverage(exchangeName)}
                      >
                        {rowBusy ? '保存中…' : '保存'}
                      </button>
                      {saved != null ? (
                        <button
                          type="button"
                          className="secondary compact"
                          disabled={rowBusy}
                          onClick={() => void onClearLeverage(exchangeName)}
                        >
                          清除
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {Number.isFinite(draftNum) && draftNum > 0 ? (
                    <p className="leverage-limit-hint">{formatLeverageHint(draftNum)}</p>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}

        {leverageError ? <p className="fail">{leverageError}</p> : null}
        {leverageInfo ? <p className="msg">{leverageInfo}</p> : null}
      </section>
    </div>
  );
}
